import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  Paper,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import SimpleCustomTable from "../../../components/table/SimpleCustomTable";
import { SimpleColumn } from "../../../components/table/interface";
import EmptyStateMessage from "../../../components/layouts/EmptyStateMessage";

type CloveClientItem = {
  id: number;
  lend_id: number;
  client_idnumber: string | number;
  client_fullname: string;
  loan_date: string;
  loan_value: number;
  balance: number;
  total_pays: number;
  clove_days: number;
};

interface ModalCloveClientsProps {
  open: boolean;
  onClose: () => void;
  collectId: number | null;
  collectName: string | null;
  date: string | null;
  showAlert: (msg: string, severity?: "error" | "info" | "success" | "warning") => void;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
  }).format(Number(value || 0));

const ModalCloveClients: React.FC<ModalCloveClientsProps> = ({
  open,
  onClose,
  collectId,
  collectName,
  date,
  showAlert,
}) => {
  const [items, setItems] = useState<CloveClientItem[]>([]);
  const [loading, setLoading] = useState(false);
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (!open || !collectId || !date) {
      return;
    }

    let active = true;
    const requestId = ++reqIdRef.current;

    const fetchData = async () => {
      setLoading(true);
      try {
        const { data } = await axios.post("/inform/summarybalancecloveclients", {
          collect_id: collectId,
          date,
        });

        if (!active || requestId !== reqIdRef.current) {
          return;
        }

        setItems(Array.isArray(data?.data) ? data.data : []);
      } catch (error) {
        if (active) {
          console.error(error);
          showAlert("No fue posible cargar los clientes clavo.", "error");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      active = false;
    };
  }, [open, collectId, date, showAlert]);

  const rows = items.map((item, index) => ({
    id: item.id || index,
    lend_id: item.lend_id,
    client_idnumber: item.client_idnumber,
    client_fullname: item.client_fullname,
    loan_date: item.loan_date,
    loan_value: item.loan_value,
    balance: item.balance,
    total_pays: item.total_pays,
    clove_days: item.clove_days,
  }));

  const columns: SimpleColumn[] = [
    { field: "lend_id", headerName: "Préstamo", minWidth: 100, align: "center" },
    { field: "client_idnumber", headerName: "Cédula", minWidth: 100, wrapText: true },
    { field: "client_fullname", headerName: "Cliente", minWidth: 140, maxWidth: 180, wrapText: true },
    { field: "loan_date", headerName: "Fecha Préstamo", minWidth: 120, align: "center" },
    {
      field: "loan_value",
      headerName: "Valor Préstamo",
      minWidth: 100,
      align: "right",
      renderCell: ({ row }) => (
        <Typography variant="body2">{formatCurrency(Number(row.loan_value ?? 0))}</Typography>
      ),
    },
    {
      field: "balance",
      headerName: "Saldo",
      minWidth: 100,
      align: "right",
      renderCell: ({ row }) => (
        <Typography variant="body2">{formatCurrency(Number(row.balance ?? 0))}</Typography>
      ),
    },
    {
      field: "total_pays",
      headerName: "Total Abonos",
      minWidth: 100,
      align: "right",
      renderCell: ({ row }) => (
        <Typography variant="body2">{formatCurrency(Number(row.total_pays ?? 0))}</Typography>
      ),
    },
    { field: "clove_days", headerName: "Días Clavo", minWidth: 110, align: "center" },
  ];

  return (
    <Modal open={open} onClose={onClose}>
      <Paper
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "95vw",
          maxWidth: 1000,
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            bgcolor: "primary.main",
            color: "#fff",
          }}
        >
          <Typography variant="h6">
            {`Cobro : ${collectName || ""} | Fecha: ${date || ""}`}
          </Typography>
          <IconButton onClick={onClose} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 2, pt: 1, display: "flex", flexDirection: "column", gap: 1, overflow: "auto" }}>
          <Box sx={{ flex: 1, minHeight: 360 }}>
            {items.length > 0 ? (
              <SimpleCustomTable
                rows={rows}
                columns={columns}
                maxHeight={500}
                showBorders
                showCount
                exportCsv
                exportFileName={`clientes-clavo-${collectId ?? ""}.csv`}
                wrapCellContent
              />
            ) : loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <EmptyStateMessage
                message="No existen clientes marcados como clavo para ese día."
                align="center"
              />
            )}
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};

export default ModalCloveClients;
